import { type FC } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/css";
import { ExternalLinkIcon, PhoneIcon } from "@snapchat/snap-design-system-icons";
import { Button, Colors, Fonts } from "@snapchat/snap-design-system";
import { generatePath, useNavigate } from "react-router-dom";
import browserIcon from "../../assets/common/browser-icon.svg";
import { Visible, LinkBack } from "../../common/components";
import { Flexbox } from "../../core/components";
import { Image } from "../../common/components/Image";
import { AbsoluteRoute } from "../../../core/routes";
import { EnableAppButton } from "../components/EnableAppButton";
import { MyLensesPlatformType } from "../../../gql-generated/types";

type TitlePaneProps = {
    title: string;
    iconUrl?: string;
    isDetailsPage?: boolean;
    appId?: string;
    hasApps: boolean;
    organizationId: string;
    platformType?: MyLensesPlatformType | null;
};

export const TitlePane: FC<TitlePaneProps> = ({
    title,
    iconUrl,
    isDetailsPage,
    appId,
    organizationId,
    hasApps,
    platformType,
}) => {
    const navigate = useNavigate();

    const handleClick = () => {
        navigate(generatePath(AbsoluteRoute.ORG_APPS_CREATE, { orgId: organizationId }));
    };

    const isWeb = platformType === MyLensesPlatformType.PlatformTypeWeb;

    return (
        <PaneContainer justifyContent="space-between">
            <LeftContainer>
                {organizationId && isDetailsPage && (
                    <LinkBackWrapper>
                        <LinkBack to={generatePath(AbsoluteRoute.ORG_APPS, { orgId: organizationId })}>
                            &lt; Back to Apps
                        </LinkBack>
                    </LinkBackWrapper>
                )}
                <TitleContainer>
                    {iconUrl && (
                        <Image
                            data-testid="app-icon"
                            src={iconUrl}
                            alt="App Detail Icon"
                            widthPx={24}
                            heightPx={24}
                            isCircle
                        />
                    )}
                    <Title data-testid="app-title">{title}</Title>
                    <Visible when={Boolean(isDetailsPage)}>
                        <Visible when={isWeb}>
                            <Image src={browserIcon} alt="browser" widthPx={18} heightPx={18} isCircle={false} />
                        </Visible>
                        <Visible when={!isWeb}>
                            <PhoneIcon size={24} className={phoneIconCss} />
                        </Visible>
                    </Visible>
                </TitleContainer>
                {isDetailsPage && <Details data-testid="app-details">Overview of your Camera Kit integration</Details>}
            </LeftContainer>
            <Visible when={!isDetailsPage}>
                <EnableAppButton
                    hasApps={hasApps}
                    organizationId={organizationId}
                    onClick={handleClick}
                    data-testid="enable-app-button"
                    isElevated
                >
                    Enable App
                </EnableAppButton>
            </Visible>
            <Visible when={Boolean(isDetailsPage) && appId !== undefined}>
                <a href={`https://kit.snapchat.com/manage/apps/${appId}`} target="_blank">
                    <Button isElevated>
                        <ExternalLinkIcon />
                        Developer Portal
                    </Button>
                </a>
            </Visible>
        </PaneContainer>
    );
};

// difference between the svg width and actual icon width
const phoneIconCss = css`
    margin-left: -7px;
`;

const PaneContainer = styled(Flexbox)`
    position: relative;
    align-items: center;
`;

const LeftContainer = styled(Flexbox)`
    flex-wrap: wrap;
    gap: 4px;
`;

const TitleContainer = styled(Flexbox)`
    gap: 8px;
    align-items: center;
    width: 100%;
`;

const LinkBackWrapper = styled.div`
    flex-basis: 100%;
    width: 100%;
    margin-bottom: 4px;
`;

const Title = styled.h2`
    ${Fonts.Heading.H300};
    margin-bottom: 0;
`;

const Details = styled.h6`
    ${Fonts.Heading.H600};
    color: ${Colors.Gray.V300};
`;
